import { SHA512 } from 'crypto-js';
import { baseUrl } from '../utils/url';
import { store } from '../store';
import { encrypt, encryptPayload } from '../utils/security';
import { IBaseApiRes } from '@interfaces/generic.interface';
import { ISystemDialog, IUserLogin } from '@store/slices/user.slice';

const headers = { 'Content-Type': 'application/json' };

export type TLoginRes = IBaseApiRes<IUserLogin>;

export type TErrorRes = IBaseApiRes<{
  error_message: string;
  level: number;
  errors: {
    error: {
      remarks: string;
    };
  }[];
  deleteSession: boolean;
  isMaintenanceMode?: number;
  systemDialogs?: ISystemDialog[];
  device: {
    os: string;
    browser: string;
  };
  mfa: {
    require: boolean;
    token: string;
  };
  usersID: string;
}>;

export const auth = {
  login: async (data: {
    username: string;
    password: string;
  }): Promise<TLoginRes | TErrorRes> => {
    try {
      const signature = SHA512(`${data.username}${data.password}`).toString();
      const res = await fetch(`${baseUrl}authentication/userCredentials`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: encryptPayload({ ...data, signature }, true) as BodyInit,
      });

      return (await res.json()) as TLoginRes | TErrorRes;
    } catch (error) {
      return error as TErrorRes;
    }
  },
  logout: async (data: { sessionID: string; token: string }) => {
    try {
      const res = await fetch(`${baseUrl}logout`, {
        method: 'POST',
        headers: {
          ...headers,
          'x-access-token': data.token,
        },
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  otp: async (token: string) => {
    try {
      const res = await fetch(`${baseUrl}authentication/resendOTP`, {
        method: 'POST',
        headers: {
          ...headers,
          'x-access-token': token,
        },
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  verifyDevice: async (data: { deviceID: string; token: string }) => {
    try {
      const res = await fetch(
        `${baseUrl}authentication/device/${data.deviceID}`,
        {
          method: 'POST',
          headers: {
            ...headers,
            'x-access-token': data.token,
          },
        },
      );

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  validateSession: async (sessionID: string) => {
    try {
      const res = await fetch(`${baseUrl}authentication/session/${sessionID}`, {
        headers,
        credentials: 'include',
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  generateOtpAlreadyLoggedIn: async (userID: string) => {
    try {
      const res = await fetch(`${baseUrl}authentication/session/${userID}`, {
        method: 'PUT',
        headers: headers,
        credentials: 'include',
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  verifyGenerateOtpAlreadyLoggedIn: async (data: {
    verificationCode: string;
    sessionID: string;
  }) => {
    try {
      const payload = {
        verificationCode: data.verificationCode,
        sessionID: data.sessionID,
      };
      const signature = SHA512(
        `${payload.verificationCode}${payload.sessionID}`,
      ).toString();
      const res = await fetch(`${baseUrl}authentication/session`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        // body: JSON.stringify({ ...data, signature }),
        body: encryptPayload({ ...payload, signature }, true) as BodyInit,
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  verifyMFAAlreadyLoggedIn: async (data: {
    verificationCode: string;
    sessionID: string;
    mfaLoginToken: string;
  }) => {
    try {
      const payload = {
        verificationCode: data.verificationCode,
        sessionID: data.sessionID,
        token: data.mfaLoginToken,
      };
      const signature = SHA512(
        `${payload.verificationCode}${payload.sessionID}${payload.token}`,
      ).toString();
      const res = await fetch(`${baseUrl}authentication/session`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: encryptPayload(
          {
            ...payload,
            signature,
            isMFA: true,
          },
          true,
        ) as BodyInit,
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
  // verifySecCode: async (secCode: string, priv: string) => {
  //   try {
  //     const { getState } = store;
  //     const data = getState();
  //     const encryptedSecCode = encrypt(secCode).replace(/\s/g, '');

  //     const res = await fetch(
  //       `${baseUrl}authentication/securityCode?data[securityCode]=${encryptedSecCode}=&data[priviledgeCode]=${priv}`,
  //       {
  //         headers: {
  //           'x-access-token': data?.user?.info?.token,
  //           propertyID: data?.properties?.selectedProperty?.value,
  //         },
  //         method: 'POST',
  //       },
  //     );
  //     return await res.json();
  //   } catch (error) {
  //     return error;
  //   }
  // },
  verifySecCode: async (secCode: string, priv: string) => {
    try {
      const { getState } = store;
      const data = getState();
      // const encryptedSecCode = encrypt(secCode).replace(/\s/g, '');
      const encryptedSecCode = encrypt(secCode);

      const res = await fetch(`${baseUrl}authentication/securityCode`, {
        headers: {
          ...headers,
          'x-access-token': data?.user?.info?.token,
        },
        method: 'POST',
        // body: JSON.stringify({
        //   propertyID: data?.properties?.selectedProperty?.value,
        //   securityCode: encryptedSecCode,
        //   priviledgeCode: priv,
        // }),
        body: encryptPayload(
          {
            propertyID: data?.properties?.selectedProperty?.value,
            securityCode: encryptedSecCode,
            priviledgeCode: priv,
          },
          true,
        ) as BodyInit,
      });
      return await res.json();
    } catch (error) {
      return error;
    }
  },
  validateModule: async (token: string) => {
    try {
      const res = await fetch(`${baseUrl}properties`, {
        headers: {
          ...headers,
          'x-access-token': token,
        },
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },

  loginMFAAuth: async (data: {
    code: string;
    mfaLoginToken?: string;
    signature?: string;
    loginToken?: string;
  }) => {
    try {
      const mfaLoginToken = data?.mfaLoginToken;
      const authToken = data?.loginToken ?? '';

      const res = await fetch(`${baseUrl}authentication/mfa/verify`, {
        method: 'POST',
        headers: {
          ...headers,
          'x-access-token': authToken,
        },
        credentials: 'include',
        body: encryptPayload(
          {
            code: data?.code,
            token: mfaLoginToken,
            signature: data?.signature || '',
          },
          true,
        ) as BodyInit,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.message ?? 'MFA verification failed');
      }

      return json;
    } catch (error) {
      throw error;
    }
  },

  setupMFAAuth: async () => {
    try {
      const { getState } = store;
      const state = getState();
      const token = state?.user?.info?.token;

      const res = await fetch(`${baseUrl}authentication/mfa/setup`, {
        method: 'POST',
        headers: {
          ...headers,
          'x-access-token': token,
        },
        credentials: 'include',
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },

  verifyMFAAuth: async ({ verificationCode }: { verificationCode: string }) => {
    try {
      const { getState } = store;
      const state = getState();
      const token = state?.user?.info?.token;
      const res = await fetch(`${baseUrl}authentication/mfa/verifyCode`, {
        method: 'POST',
        headers: {
          ...headers,
          'x-access-token': token,
        },
        credentials: 'include',
        body: JSON.stringify({ code: verificationCode }),
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },

  disableMFAAuth: async () => {
    try {
      const { getState } = store;
      const state = getState();
      const token = state?.user?.info?.token;
      const res = await fetch(`${baseUrl}authentication/mfa/disable`, {
        method: 'POST',
        headers: {
          ...headers,
          'x-access-token': token,
        },
        credentials: 'include',
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },

  updateSessionMfa: async (userID: string) => {
    try {
      const res = await fetch(`${baseUrl}session/${userID}/otp`, {
        method: 'PUT',
        headers,
        credentials: 'include',
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },

  updateSessionLoggedInMfa: async (userID: string) => {
    try {
      const res = await fetch(`${baseUrl}session/${userID}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },

  generateForceOTPMfa: async (userID: string) => {
    try {
      const res = await fetch(`${baseUrl}session/${userID}/force-otp`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ isMFA: false }),
      });

      return await res.json();
    } catch (error) {
      return error;
    }
  },
};
